import {
  Autocomplete,
  Box,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import {
  PHONE_COUNTRIES,
  type PhoneCountry,
  composePhone,
  phoneCountryMatches,
  splitPhone,
} from "../utils/phone-countries";
import { SelectChevron, fieldInputSx } from "./field-shell";

const codeLabel = (country: PhoneCountry): string =>
  `${country.flag} +${country.callingCode}`;

/**
 * Country-code picker + number input per the design, but a SINGLE string value outward —
 * the submission key and the crm-backend telephone reconcile are untouched. An untouched
 * legacy value (no "+code" prefix) never fires onChange; any edit saves normalized
 * "+20…" (leading zeros stripped) while the typed digits stay visible as-is.
 *
 * The picker offers every country except Israel (product ask 2026-09-02; the CRM's other phone
 * inputs exclude it the same way). It is an Autocomplete, not a menu: ~240 entries are found by
 * typing a name, ISO code or calling code, and the collapsed input shows only "flag +code" so the
 * row keeps its width.
 */
const PhoneField = ({
  value,
  onChange,
  disabled,
  ariaLabel,
}: {
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
  ariaLabel: string;
}) => {
  const theme = useTheme();
  const [country, setCountry] = useState(() => splitPhone(value).country);
  const [localDraft, setLocalDraft] = useState(() => splitPhone(value).local);

  // external value changes (submission reload) resync the split; React's identical-state
  // bail keeps unprefixed legacy values from looping
  useEffect(() => {
    if (value !== composePhone(country, localDraft)) {
      const next = splitPhone(value);
      setCountry(next.country);
      setLocalDraft(next.local);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <Stack direction="row" spacing={1}>
      <Autocomplete
        size="small"
        options={PHONE_COUNTRIES}
        value={country}
        onChange={(_, next) => {
          if (!next) return;
          setCountry(next);
          if (composePhone(next, localDraft)) {
            onChange(composePhone(next, localDraft));
          }
        }}
        disabled={disabled}
        disableClearable
        autoHighlight
        popupIcon={<SelectChevron />}
        getOptionLabel={codeLabel}
        isOptionEqualToValue={(option, selected) => option.iso === selected.iso}
        filterOptions={(options, { inputValue }) =>
          options.filter((option) => phoneCountryMatches(option, inputValue))
        }
        renderOption={(props, option) => (
          <Box component="li" {...props} key={option.iso}>
            <Typography component="span" sx={{ fontSize: 14, mr: 1 }}>
              {option.flag} {option.name}
            </Typography>
            <Typography
              component="span"
              sx={{ fontSize: 14, color: theme.nawy.colors.semantic.text.secondary, ml: "auto" }}
            >
              +{option.callingCode}
            </Typography>
          </Box>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            sx={fieldInputSx(theme)}
            slotProps={{
              htmlInput: {
                ...params.inputProps,
                "aria-label": `${ariaLabel} country code`,
              },
            }}
          />
        )}
        slotProps={{ paper: { sx: { minWidth: 280 } } }}
        sx={{
          width: 128,
          flexShrink: 0,
          "& .MuiAutocomplete-popupIndicator": { mr: 0.25 },
        }}
      />
      <TextField
        size="small"
        type="tel"
        value={localDraft}
        onChange={(event) => {
          setLocalDraft(event.target.value);
          onChange(composePhone(country, event.target.value));
        }}
        disabled={disabled}
        sx={{ ...fieldInputSx(theme), flex: 1 }}
        slotProps={{ htmlInput: { "aria-label": ariaLabel } }}
      />
    </Stack>
  );
};

export default PhoneField;
